import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Button, Box, Paper, Typography, IconButton, TextField, InputAdornment,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, Stack
} from '@mui/material';
import { Category, Edit, DeleteOutline, Search } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, GRUPOS_LISTAR, GRUPO_ELIMINAR } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import TituloPagina from './utils/TituloPagina.jsx';
import ExportToolbar from './utils/ExportToolbar.jsx';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const esSi = (v) => String(v).toUpperCase() === 'SI';

function GruposList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [grupos, setGrupos] = useState([]);
  const [q, setQ] = useState('');
  const [eliminar, setEliminar] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchGrupos = useCallback(() => {
    setLoading(true);
    axios.get(API_BASE_URL + GRUPOS_LISTAR, { headers: authHeader() })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setGrupos(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setGrupos([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchGrupos(); }, [fetchGrupos]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return grupos;
    return grupos.filter((g) =>
      String(g.grudescrip || '').toLowerCase().includes(t) || String(g.grupo || '').includes(t));
  }, [grupos, q]);

  const confirmarEliminar = () => {
    if (!eliminar) return;
    setErrorMsg('');
    axios.post(API_BASE_URL + GRUPO_ELIMINAR, { id: eliminar.id }, { headers: authHeader() })
      .then((res) => {
        if ((res.data?.status ?? res.data?.status) === 'error') {
          setErrorMsg(res.data?.message || 'No se pudo eliminar.');
        } else {
          setEliminar(null); fetchGrupos();
        }
      })
      .catch((e) => setErrorMsg(e.response?.data?.message || 'No se pudo eliminar.'));
  };

  const columns = [
    { field: 'grupo', headerName: 'N°', width: 70 },
    { field: 'grudescrip', headerName: 'Descripción', flex: 1, minWidth: 180 },
    { field: 'gruorden', headerName: 'Orden', width: 90, align: 'center', headerAlign: 'center' },
    {
      field: 'gruventa', headerName: 'Se registra', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (p) => <Chip size="small" label={esSi(p.row.gruventa) ? 'Sí' : 'No'} color={esSi(p.row.gruventa) ? 'success' : 'default'} sx={{ height: 22 }} />,
    },
    {
      field: 'grustock', headerName: 'Stock', width: 90, align: 'center', headerAlign: 'center',
      renderCell: (p) => <Chip size="small" label={esSi(p.row.grustock) ? 'Sí' : 'No'} color={esSi(p.row.grustock) ? 'info' : 'default'} sx={{ height: 22 }} />,
    },
    { field: 'productos', headerName: 'Productos', width: 100, align: 'center', headerAlign: 'center' },
    {
      field: 'actions', headerName: '', width: 100, sortable: false, filterable: false, align: 'right',
      renderCell: (p) => (
        <>
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/grupo?id=${p.row.id}`); }}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setEliminar(p.row); setErrorMsg(''); }}><DeleteOutline fontSize="small" /></IconButton>
        </>
      ),
    },
  ];

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={Category}
          titulo="Grupos"
          subtitulo={`${filtered.length} de ${grupos.length} grupo${grupos.length !== 1 ? 's' : ''}`}
          ayuda="'Se registra' define si el grupo aparece para vender en la pantalla de Registrar. El orden define en qué posición aparece cada grupo ahí."
          accion={<Button variant="contained" color="primary" onClick={() => navigate('/grupo?mode=create')}>Nuevo grupo</Button>}
        />

        <Box sx={{ mb: 2, maxWidth: 360 }}>
          <TextField
            size="small" fullWidth placeholder="Buscar por descripción o número…"
            value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
        </Box>

        {loading ? (
          <Cargando />
        ) : isMobile ? (
          <Stack spacing={1}>
            {filtered.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin resultados.</Typography>}
            {filtered.map((g) => (
              <Paper key={g.id} variant="outlined" onClick={() => navigate(`/grupo?id=${g.id}`)}
                sx={{ p: 1.2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.grudescrip}</Typography>
                  <Typography variant="caption" color="text.secondary">N° {g.grupo} · orden {g.gruorden} · {g.productos} prod.</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Chip size="small" label={`Registra: ${esSi(g.gruventa) ? 'Sí' : 'No'}`} color={esSi(g.gruventa) ? 'success' : 'default'} sx={{ height: 20 }} />
                    <Chip size="small" label={`Stock: ${esSi(g.grustock) ? 'Sí' : 'No'}`} color={esSi(g.grustock) ? 'info' : 'default'} sx={{ height: 20 }} />
                  </Box>
                </Box>
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setEliminar(g); setErrorMsg(''); }}><DeleteOutline fontSize="small" /></IconButton>
              </Paper>
            ))}
          </Stack>
        ) : (
          <div style={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={filtered} columns={columns} pageSize={10} rowsPerPageOptions={[10, 25, 50]}
              getRowId={(r) => r.id} disableSelectionOnClick rowHeight={48}
              onRowClick={(params) => navigate(`/grupo?id=${params.row.id}`)}
              sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
              components={{ Toolbar: ExportToolbar }}
            />
          </div>
        )}
      </Box>

      <Dialog open={!!eliminar} onClose={() => setEliminar(null)}>
        <DialogTitle>Eliminar grupo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">¿Eliminar el grupo <strong>{eliminar?.grudescrip}</strong>?</Typography>
          {errorMsg && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{errorMsg}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminar(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarEliminar}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default GruposList;
